import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@/context/ThemeContext';
import { useStudentAuth } from '@/context/StudentAuthContext';
import { 
  ArrowLeft,
  Search,
  FileText,
  Calendar,
  Clock,
  CheckCircle,
  XCircle,
  Loader2,
  Download,
  Filter,
  RefreshCw
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

const API_URL = window.location.origin;

const SearchAuditLog = () => {
  const { isDark } = useTheme();
  const { user, loading: authLoading, isAuthenticated } = useStudentAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [searchStatus, setSearchStatus] = useState(null);
  const [savedSearches, setSavedSearches] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/uac-desk/login');
    }
  }, [authLoading, isAuthenticated, navigate]);
  
  useEffect(() => {
    const loadData = async () => {
      if (!user?.user_id) return;
      setLoading(true);
      
      try {
        // Load search status
        const statusRes = await fetch(`${API_URL}/api/search/status/${user.user_id}`);
        if (statusRes.ok) {
          setSearchStatus(await statusRes.json());
        }
        
        // Load saved searches
        const savedRes = await fetch(`${API_URL}/api/search/saved/${user.user_id}`);
        if (savedRes.ok) {
          const data = await savedRes.json();
          setSavedSearches(data.saved_searches || []);
        }
        
        // Load audit logs
        const auditRes = await fetch(`${API_URL}/api/search/audit/user/${user.user_id}`);
        if (auditRes.ok) {
          const data = await auditRes.json();
          setAuditLogs(data.audit_logs || []);
        }
      } catch (error) {
        console.error('Error loading audit data:', error);
      }
      
      setLoading(false);
    };
    
    loadData();
  }, [user]);
  
  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  const getActionLabel = (action) => {
    const labels = {
      'search_session_started': 'Started Search',
      'search_saved': 'Saved Search',
      'search_cleared': 'Cleared Search',
      'search_change_limit_reached': 'Change Limit Reached',
      'document_gate_check': 'Document Verification'
    };
    return labels[action] || action;
  };
  
  const getActionColor = (action) => {
    const colors = {
      'search_session_started': 'bg-blue-100 text-blue-700',
      'search_saved': 'bg-green-100 text-green-700',
      'search_cleared': 'bg-amber-100 text-amber-700',
      'search_change_limit_reached': 'bg-rose-100 text-rose-700',
      'document_gate_check': 'bg-purple-100 text-purple-700'
    };
    return colors[action] || 'bg-gray-100 text-gray-700';
  };

  if (authLoading || loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDark ? 'bg-[#1a2830]' : 'bg-ux-slate-light'}`}>
        <Loader2 className="w-8 h-8 animate-spin text-ux-cyan" />
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${isDark ? 'bg-[#1a2830]' : 'bg-ux-slate-light'}`}>
      {/* Header */}
      <header className={`sticky top-0 z-50 border-b ${isDark ? 'bg-[#233842] border-white/10' : 'bg-white border-ux-slate-light'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Button variant="ghost" onClick={() => navigate('/uac-desk/dashboard')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>
            <h1 className={`font-bold text-lg ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
              Search History & Audit Log
            </h1>
            <div className="w-32" />
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Search Status Summary */}
        <div className="grid md:grid-cols-3 gap-4 mb-8">
          <Card className={isDark ? 'bg-[#233842] border-white/10' : ''}>
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-ux-cyan to-ux-blue flex items-center justify-center">
                  <Search className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className={`text-sm ${isDark ? 'text-white/60' : 'text-gray-500'}`}>Searches Remaining</p>
                  <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
                    {searchStatus?.searches_remaining || 0} / 5
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className={isDark ? 'bg-[#233842] border-white/10' : ''}>
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-ux-green to-emerald-500 flex items-center justify-center">
                  <FileText className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className={`text-sm ${isDark ? 'text-white/60' : 'text-gray-500'}`}>Saved Searches</p>
                  <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
                    {savedSearches.length}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className={isDark ? 'bg-[#233842] border-white/10' : ''}>
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-ux-violet to-purple-500 flex items-center justify-center">
                  <Clock className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className={`text-sm ${isDark ? 'text-white/60' : 'text-gray-500'}`}>Total Activities</p>
                  <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
                    {auditLogs.length}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Saved Searches */}
        <Card className={`mb-8 ${isDark ? 'bg-[#233842] border-white/10' : ''}`}>
          <CardHeader>
            <CardTitle className={`flex items-center gap-2 ${isDark ? 'text-white' : ''}`}>
              <FileText className="w-5 h-5 text-ux-cyan" />
              Your Saved Searches
            </CardTitle>
          </CardHeader>
          <CardContent>
            {savedSearches.length === 0 ? (
              <p className={`text-center py-8 ${isDark ? 'text-white/60' : 'text-gray-500'}`}>
                No saved searches yet. Start a search and save it to see it here.
              </p>
            ) : (
              <div className="space-y-3">
                {savedSearches.map((search, idx) => (
                  <div 
                    key={search.session_id || idx}
                    className={`p-4 rounded-xl border ${isDark ? 'border-white/10 bg-white/5' : 'border-gray-200 bg-gray-50'}`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className={`font-semibold ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
                          {search.session_name || `Search ${idx + 1}`}
                        </h4>
                        <div className="flex items-center gap-4 mt-1">
                          <span className={`text-sm ${isDark ? 'text-white/60' : 'text-gray-500'}`}>
                            {search.search_type === 'fast_track' ? 'Fast-Track Application' : 
                             search.search_type === 'ai_finder' ? 'AI University Finder' : 
                             search.search_type}
                          </span>
                          {search.course && (
                            <span className={`text-sm ${isDark ? 'text-white/60' : 'text-gray-500'}`}>
                              • {search.course}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        <Badge className={`${search.completion_percentage >= 75 ? 'bg-green-100 text-green-700' : search.completion_percentage >= 50 ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'}`}>
                          {search.completion_percentage || 0}% Complete
                        </Badge>
                        <p className={`text-xs mt-1 ${isDark ? 'text-white/40' : 'text-gray-400'}`}>
                          Saved {formatDate(search.saved_at)}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex gap-2 mt-3">
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => navigate('/uac-desk/fast-track', { state: { resumeSession: search.session_id } })}
                        className={isDark ? 'border-white/20' : ''}
                      >
                        <RefreshCw className="w-3 h-3 mr-1" />
                        Resume
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Audit Log */}
        <Card className={isDark ? 'bg-[#233842] border-white/10' : ''}>
          <CardHeader>
            <CardTitle className={`flex items-center gap-2 ${isDark ? 'text-white' : ''}`}>
              <Clock className="w-5 h-5 text-ux-cyan" />
              Activity Log
            </CardTitle>
          </CardHeader>
          <CardContent>
            {auditLogs.length === 0 ? (
              <p className={`text-center py-8 ${isDark ? 'text-white/60' : 'text-gray-500'}`}>
                No activity recorded yet.
              </p>
            ) : (
              <div className="space-y-2">
                {auditLogs.map((log, idx) => (
                  <div 
                    key={log.audit_id || idx}
                    className={`flex items-center justify-between p-3 rounded-lg ${isDark ? 'bg-white/5' : 'bg-gray-50'}`}
                  >
                    <div className="flex items-center gap-3">
                      <Badge className={getActionColor(log.action)}>
                        {getActionLabel(log.action)}
                      </Badge>
                      {log.details?.session_name && (
                        <span className={`text-sm ${isDark ? 'text-white/70' : 'text-gray-600'}`}>
                          "{log.details.session_name}"
                        </span>
                      )}
                      {log.details?.search_type && (
                        <span className={`text-xs px-2 py-0.5 rounded ${isDark ? 'bg-white/10 text-white/60' : 'bg-gray-200 text-gray-600'}`}>
                          {log.details.search_type}
                        </span>
                      )}
                    </div>
                    <span className={`text-xs ${isDark ? 'text-white/40' : 'text-gray-400'}`}>
                      {formatDate(log.timestamp)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default SearchAuditLog;
